import { coverAiLimit } from './coverEditorPlans';

/**
 * The only source of Music24 plan limits. music24-api builds the licence answer from it, musicapi
 * and the apps read the same numbers, and the website prints them. Change a limit here, nowhere else.
 */
/** The place of a workspace in the render queue: paid plans first, Free and the trial last. */
export type RenderLane = 'first' | 'normal' | 'last';

/**
 * `plug` is the Plug plan ($299): the Pro plan for one artist plus Outreach (250 pitches a month,
 * autopilot, two own mailboxes). It sells on the web only (spec 2026-10-13 Outreach pricing).
 */
export type Music24PlanTier = 'free' | 'trial' | 'artist' | 'pro' | 'plug' | 'manager' | 'label';

/** Every tier, in rank order (`MUSIC24_TIER_RANK`). */
export const MUSIC24_PLAN_TIERS: readonly Music24PlanTier[] = [
  'free',
  'trial',
  'artist',
  'pro',
  'plug',
  'manager',
  'label',
];

export const MUSIC24_TIER_RANK: Record<Music24PlanTier, number> = {
  free: 0,
  trial: 1,
  artist: 2,
  pro: 3,
  plug: 4,
  manager: 5,
  label: 6,
};

export const MUSIC24_TRIAL_DAYS = 7;

/** The paid tier whose limits the 7-day signup trial gives. */
export const MUSIC24_TRIAL_LEVEL_TIER: Exclude<Music24PlanTier, 'free' | 'trial'> = 'pro';

/** `null` in a limit means unlimited. */
export interface Music24Capabilities {
  plan: Music24PlanTier;
  /** The artists the workspace can have: `includedArtists` plus the paid extra artists. */
  artistsLimit: number | null;
  /** The artists the plan price includes: 1 on every plan (owner decision 2026-10-23). */
  includedArtists: number;
  /**
   * The most paid extra artists on top of `includedArtists`. Null is no cap, 0 is none. Each extra
   * artist gets a full copy of the per-artist limits (`MUSIC24_PER_ARTIST_LIMITS`).
   */
  extraArtistLimit: number | null;
  /** Team seats. By tier, not by artist (owner decision 2026-10-23, D4). */
  seatsLimit: number | null;
  publishedPagesLimit: number | null;
  /**
   * The plan removes the Music24 watermark from the assets (owner 2026-10-16): false on Free,
   * true on the paid plans. False on the trial too (owner 2026-10-19,
   * `MUSIC24_TRIAL_PRO_EXCEPTIONS`): its SmartLink pages keep the Music24 badge, and its files stay
   * watermarked previews (`previewRenders`, `clipWatermark`). The link preview image (og:image) of a page reads this
   * flag. The SmartLink footer "Made with Music24" shows on every plan; this flag does not hide
   * it. The name stays for the JWT and the native apps.
   */
  canRemoveBranding: boolean;
  canExportFans: boolean;
  /** Pre-save campaigns and Pre-save Subscriptions (owner 2026-10-09: paid plans and the trial only). */
  canUsePresave: boolean;
  canUseCustomDomain: boolean;
  /**
   * Connect, publish and the daily read of X (owner decision 2026-10-22: Pro and up). The X API
   * charges Music24 for every call, so the trial does not get it (`MUSIC24_TRIAL_PRO_EXCEPTIONS`).
   */
  canUseX: boolean;
  /** The API posts a month of one X account (owner decision 2026-10-22). 0 without `canUseX`. */
  xPostsPerMonth: number;
  historyDaysLimit: number;
  maxOffset: number;
  dailyBrowseCredits: number;
  artistsFollowLimit: number;
  tracksFollowLimit: number;
  realtimeAlerts: boolean;
  artistPortalHistoryDays: number | null;
  /** The social accounts of one artist. Null is no limit (Pro and up, owner decision 2026-10-19). */
  socialAccountsLimit: number | null;
  scheduledPostsPerMonth: number | null;
  /**
   * One pool for clips, reels and kit videos. A short video costs 1, a full song costs 3
   * (`VIDEO_CREDIT_COST`). In the plan table it is the value for one artist; each extra artist adds
   * the same number again (`withExtraArtists` in fym-common).
   */
  videoCreditsPerMonth: number | null;
  /**
   * The credits that each artist adds to the pool: the `videoCreditsPerMonth` of one artist on a
   * plan that sells extra artists. Null on a plan with no extra artists (Free, the trial, Plug).
   */
  videoCreditsPerArtist: number | null;
  canRenderFullSongVideos: boolean;
  /** AI clips for the music video editor (6 credits each, `VIDEO_CREDIT_COST.aiClip`). Paid plans only. */
  canMakeAiFootage: boolean;
  clipWatermark: boolean;
  /** Free and trial renders are previews: half-size images, 540p videos, always a watermark. */
  previewRenders: boolean;
  /** The place of the workspace in the render queue. */
  renderLane: RenderLane;
  /** The songs with a kit at one time. Null is no limit. */
  activeReleasesLimit: number | null;
  /** Spike and evergreen rollouts for old songs. */
  canPushOldSongs: boolean;
  /** Palette, logo, font and instructions of a kit. */
  canCustomizeKitLook: boolean;
  canDownloadKitZip: boolean;
  /** The plan part of the earnings gate. The member rule stays. */
  canSeeEarnings: boolean;
  /** The split of streams by store and country. */
  canSplitStreaming: boolean;
  aiInsightsPerMonth: number | null;
  canFilterGeographically: boolean;
  canViewListeningInsights: boolean;
  canExport: boolean;
  canFilterByMusicService: boolean;
  canAccessApi: boolean;
  /**
   * Every plan includes AI Reels. Only a caller with no licence gets false
   * (`music24NoCapabilities` in fym-common).
   */
  canUseAiReels: boolean;
  /**
   * The most posts a day of an Auto campaign (1, 2 or 3; owner decision 2026-10-19). Each post is
   * its own AI reel. A higher choice is a plan feature: the API refuses it with
   * `M24_LIMIT_FEATURE` `reelsPlanPostsPerDay`, and the clients open the plan offer.
   */
  reelsPlanMaxPostsPerDay: 1 | 2 | 3;
  /** Not a plan feature. music24-api overrides it per licence (passwordless-signup flag). */
  canViewTrackedPlaylists: boolean;
  /**
   * DEPRECATED (owner decision 2026-10-27: Outreach is open to every workspace). True on every plan
   * row and the trial; false only for a caller with no licence (`music24NoCapabilities`). Shipped
   * native builds read it to show Outreach, so keep it in the licence answer and the JWT. New code
   * reads the Outreach fields of the plan (`outreachPitchesPerDay`), not this flag.
   */
  canUseOutreach: boolean;
  /**
   * Auto upload of a song to a store (SoundCloud first; spec 2026-10-15). It gates the upload only:
   * the services panel, the store connections and their numbers are for every user. Not a plan
   * feature: false on every plan and the trial. music24-api sets it per licence: true when the
   * workspace owner has `UserModel.music24AutoUploadAccess` (music24-admin "Auto upload access"),
   * or when `MUSIC24_AUTO_UPLOAD_ALLOW_ALL=true`.
   */
  canUseAutoUpload: boolean;
  /**
   * The v1 pitch credits of a month (spec 2026-10-13). Outreach v2 has no pitch credit, no reveal
   * and no pitch pack: no Outreach route reads this field, and `outreach.revealPacks.*` is gone.
   * The pricing branch keeps or deletes it (final review M8); the pitches a day are
   * `outreachPitchesPerDay`.
   */
  outreachRevealsPerMonth: number;
  /**
   * A technical cap on the Outreach emails of a month (pitches and follow-ups). It is not the
   * price: the pitch credits are. The number is for the whole workspace, also on a team plan.
   */
  outreachSendsPerMonth: number;
  /** Connected Outreach mailboxes (IMAP/SMTP). Per artist on a team plan. */
  outreachMailboxes: number;
  /**
   * The pitches that Plug sends each day for one artist (Outreach v2). Per artist on a team plan:
   * read `outreachPitchesPerDay()` of `outreach/dailyPitches.ts`. Follow-ups do not count. 0 on
   * Free: Free cannot pitch.
   */
  outreachPitchesPerDay: number;
  /** Autopilot mode of an Outreach campaign (Plug and Label). */
  canUseOutreachAutopilot: boolean;
  /** The CSV export of the Outreach contacts (Pro and up). */
  canExportOutreachContacts: boolean;
  /** Outreach contacts shared across the artists of a workspace (Manager and Label). */
  canShareOutreachContacts: boolean;
  /** The most targets that Find shows for each kind. Null is no cap. */
  outreachFindTopN: number | null;
}

/** The fields that Artist, Pro, Manager and Label share. */
const paidBase = {
  publishedPagesLimit: null,
  canRemoveBranding: true,
  canExportFans: true,
  canUsePresave: true,
  artistPortalHistoryDays: null,
  realtimeAlerts: true,
  clipWatermark: false,
  previewRenders: false,
  canPushOldSongs: true,
  canCustomizeKitLook: true,
  canDownloadKitZip: true,
  canSeeEarnings: true,
  canSplitStreaming: true,
  canUseAiReels: true,
  canMakeAiFootage: true,
  canViewTrackedPlaylists: false,
  canUseOutreach: true,
  canUseAutoUpload: false,
} as const;

/** The tiers with their own limits. The trial has none: it copies Pro (see below). */
const music24OwnTierCapabilities: Record<Exclude<Music24PlanTier, 'trial'>, Music24Capabilities> = {
  free: {
    plan: 'free',
    artistsLimit: 1,
    includedArtists: 1,
    extraArtistLimit: 0,
    seatsLimit: 1,
    publishedPagesLimit: 3,
    canRemoveBranding: false,
    canExportFans: false,
    canUsePresave: false,
    canUseCustomDomain: false,
    canUseX: false,
    xPostsPerMonth: 0,
    historyDaysLimit: 7,
    maxOffset: 10,
    dailyBrowseCredits: 5,
    artistsFollowLimit: 1,
    tracksFollowLimit: 5,
    realtimeAlerts: false,
    artistPortalHistoryDays: 7,
    socialAccountsLimit: 1,
    scheduledPostsPerMonth: 10,
    videoCreditsPerMonth: 10,
    videoCreditsPerArtist: null,
    canRenderFullSongVideos: false,
    canMakeAiFootage: false,
    clipWatermark: true,
    previewRenders: true,
    renderLane: 'last',
    activeReleasesLimit: 1,
    canPushOldSongs: false,
    canCustomizeKitLook: false,
    canDownloadKitZip: false,
    canSeeEarnings: false,
    canSplitStreaming: false,
    aiInsightsPerMonth: 0,
    canFilterGeographically: false,
    canViewListeningInsights: false,
    canExport: false,
    canFilterByMusicService: false,
    canAccessApi: false,
    canUseAiReels: true,
    reelsPlanMaxPostsPerDay: 1,
    canViewTrackedPlaylists: false,
    canUseOutreach: true,
    canUseAutoUpload: false,
    outreachRevealsPerMonth: 0,
    outreachSendsPerMonth: 0,
    outreachMailboxes: 0,
    outreachPitchesPerDay: 0,
    canUseOutreachAutopilot: false,
    canExportOutreachContacts: false,
    canShareOutreachContacts: false,
    outreachFindTopN: 10,
  },
  artist: {
    ...paidBase,
    plan: 'artist',
    artistsLimit: 1,
    includedArtists: 1,
    extraArtistLimit: 9,
    seatsLimit: 1,
    canUseCustomDomain: false,
    canUseX: false,
    xPostsPerMonth: 0,
    historyDaysLimit: 90,
    maxOffset: 40,
    dailyBrowseCredits: 25,
    artistsFollowLimit: 5,
    tracksFollowLimit: 50,
    socialAccountsLimit: 3,
    scheduledPostsPerMonth: 60,
    videoCreditsPerMonth: 20,
    videoCreditsPerArtist: 20,
    canRenderFullSongVideos: false,
    renderLane: 'normal',
    activeReleasesLimit: 2,
    aiInsightsPerMonth: 20,
    canFilterGeographically: false,
    canViewListeningInsights: false,
    canExport: false,
    canFilterByMusicService: false,
    canAccessApi: false,
    reelsPlanMaxPostsPerDay: 3,
    outreachRevealsPerMonth: 0,
    outreachSendsPerMonth: 300,
    outreachMailboxes: 1,
    outreachPitchesPerDay: 1,
    canUseOutreachAutopilot: false,
    canExportOutreachContacts: false,
    canShareOutreachContacts: false,
    outreachFindTopN: null,
  },
  pro: {
    ...paidBase,
    plan: 'pro',
    artistsLimit: 1,
    includedArtists: 1,
    extraArtistLimit: 9,
    seatsLimit: 3,
    canUseCustomDomain: true,
    canUseX: true,
    xPostsPerMonth: 30,
    historyDaysLimit: 365,
    maxOffset: 90,
    dailyBrowseCredits: 100,
    artistsFollowLimit: 25,
    tracksFollowLimit: 250,
    socialAccountsLimit: null,
    scheduledPostsPerMonth: 200,
    videoCreditsPerMonth: 100,
    videoCreditsPerArtist: 100,
    canRenderFullSongVideos: true,
    renderLane: 'first',
    activeReleasesLimit: null,
    aiInsightsPerMonth: 100,
    canFilterGeographically: true,
    canViewListeningInsights: true,
    canExport: true,
    canFilterByMusicService: false,
    canAccessApi: false,
    reelsPlanMaxPostsPerDay: 3,
    outreachRevealsPerMonth: 0,
    outreachSendsPerMonth: 300,
    outreachMailboxes: 1,
    outreachPitchesPerDay: 5,
    canUseOutreachAutopilot: false,
    canExportOutreachContacts: true,
    canShareOutreachContacts: false,
    outreachFindTopN: null,
  },
  // Plug: the Pro row for one artist, plus the Outreach numbers. Keep every other field equal to Pro,
  // except the extra artists: Plug is not sold any more and sells no extra artist (owner 2026-10-23).
  plug: {
    ...paidBase,
    plan: 'plug',
    artistsLimit: 1,
    includedArtists: 1,
    extraArtistLimit: 0,
    seatsLimit: 3,
    canUseCustomDomain: true,
    canUseX: true,
    xPostsPerMonth: 30,
    historyDaysLimit: 365,
    maxOffset: 90,
    dailyBrowseCredits: 100,
    artistsFollowLimit: 25,
    tracksFollowLimit: 250,
    socialAccountsLimit: null,
    scheduledPostsPerMonth: 200,
    videoCreditsPerMonth: 100,
    videoCreditsPerArtist: null,
    canRenderFullSongVideos: true,
    renderLane: 'first',
    activeReleasesLimit: null,
    aiInsightsPerMonth: 100,
    canFilterGeographically: true,
    canViewListeningInsights: true,
    canExport: true,
    canFilterByMusicService: false,
    canAccessApi: false,
    reelsPlanMaxPostsPerDay: 3,
    outreachRevealsPerMonth: 250,
    outreachSendsPerMonth: 500,
    outreachMailboxes: 2,
    outreachPitchesPerDay: 10,
    canUseOutreachAutopilot: true,
    canExportOutreachContacts: true,
    canShareOutreachContacts: false,
    outreachFindTopN: null,
  },
  manager: {
    ...paidBase,
    plan: 'manager',
    artistsLimit: 1,
    includedArtists: 1,
    extraArtistLimit: 9,
    seatsLimit: 5,
    canUseCustomDomain: true,
    canUseX: true,
    xPostsPerMonth: 30,
    historyDaysLimit: 365,
    maxOffset: 90,
    dailyBrowseCredits: 100,
    artistsFollowLimit: 25,
    tracksFollowLimit: 250,
    socialAccountsLimit: null,
    scheduledPostsPerMonth: 300,
    videoCreditsPerMonth: 200,
    videoCreditsPerArtist: 200,
    canRenderFullSongVideos: true,
    renderLane: 'first',
    activeReleasesLimit: null,
    aiInsightsPerMonth: 100,
    canFilterGeographically: true,
    canViewListeningInsights: true,
    canExport: true,
    canFilterByMusicService: false,
    canAccessApi: true,
    reelsPlanMaxPostsPerDay: 3,
    // Pitch credits and sends are for the whole workspace. The own mailboxes are per artist.
    outreachRevealsPerMonth: 0,
    outreachSendsPerMonth: 300,
    outreachMailboxes: 1,
    outreachPitchesPerDay: 10,
    canUseOutreachAutopilot: false,
    canExportOutreachContacts: true,
    canShareOutreachContacts: true,
    outreachFindTopN: null,
  },
  label: {
    ...paidBase,
    plan: 'label',
    artistsLimit: 1,
    includedArtists: 1,
    extraArtistLimit: null,
    seatsLimit: null,
    canUseCustomDomain: true,
    canUseX: true,
    xPostsPerMonth: 30,
    historyDaysLimit: 365,
    maxOffset: 90,
    dailyBrowseCredits: 500,
    artistsFollowLimit: 100,
    tracksFollowLimit: 1000,
    socialAccountsLimit: null,
    scheduledPostsPerMonth: 600,
    videoCreditsPerMonth: 400,
    videoCreditsPerArtist: 400,
    canRenderFullSongVideos: true,
    renderLane: 'first',
    activeReleasesLimit: null,
    aiInsightsPerMonth: 500,
    canFilterGeographically: true,
    canViewListeningInsights: true,
    canExport: true,
    canFilterByMusicService: true,
    canAccessApi: true,
    reelsPlanMaxPostsPerDay: 3,
    // 100 pitch credits a month for the whole workspace. The own mailboxes are per artist.
    outreachRevealsPerMonth: 100,
    outreachSendsPerMonth: 500,
    outreachMailboxes: 1,
    outreachPitchesPerDay: 15,
    canUseOutreachAutopilot: true,
    canExportOutreachContacts: true,
    canShareOutreachContacts: true,
    outreachFindTopN: null,
  },
};

/** Video credits of the 7-day signup trial. */
export const MUSIC24_TRIAL_VIDEO_CREDITS = 15;

/**
 * The Outreach numbers of the trial (spec 2026-10-13, Trial column): the trial reveals nothing.
 * Outreach v2 (2026-10-23) gives the trial 1 pitch a day (`outreachPitchesPerDay`), so the trial
 * connects one mailbox and sends up to 30 pitches in its month (owner decision). They are the
 * only trial values that are not the Pro values. They are applied after the trial row below, so the
 * trial row keeps the shape that the native `PlanTableParityTest` tests read.
 */
export const MUSIC24_TRIAL_OUTREACH = {
  outreachRevealsPerMonth: 0,
  outreachSendsPerMonth: 30,
  outreachMailboxes: 1,
  outreachPitchesPerDay: 1,
  canUseOutreachAutopilot: false,
  canExportOutreachContacts: false,
} as const;

const music24PlanCapabilitiesBeforeOutreach: Record<Music24PlanTier, Music24Capabilities> = {
  ...music24OwnTierCapabilities,
  // The 7-day signup trial is the Pro plan with 15 video credits, a watermark and preview renders.
  // Do not give the trial its own limits: change the Pro plan and the trial follows.
  // `MUSIC24_TRIAL_LEVEL_TIER` names the same plan. The native `PlanTableParityTest` tests read this line.
  // The cardless trial makes no AI clips (spec 2026-10-09 music video editor, D10): each costs us a Veo call.
  trial: {
    ...music24OwnTierCapabilities.pro,
    plan: 'trial',
    videoCreditsPerMonth: MUSIC24_TRIAL_VIDEO_CREDITS,
    clipWatermark: true,
    previewRenders: true,
    renderLane: 'last',
    canMakeAiFootage: false,
  },
};

/**
 * The Pro fields that the trial does not get (owner decisions 2026-10-19): no branding removal and
 * no custom domain. No X (owner decision 2026-10-22). No extra artists (owner decision 2026-10-23:
 * the trial is one artist; extra artists are for sale on a paid plan only). Every other field follows Pro. They are applied after the trial row, like
 * `MUSIC24_TRIAL_OUTREACH`, so the trial row keeps the shape that the native `PlanTableParityTest`
 * tests read; those tests read this object too.
 */
export const MUSIC24_TRIAL_PRO_EXCEPTIONS = {
  canRemoveBranding: false,
  canUseCustomDomain: false,
  canUseX: false,
  xPostsPerMonth: 0,
  extraArtistLimit: 0,
  videoCreditsPerArtist: null,
} as const;

/**
 * The most posts a day of an Auto campaign on the trial (owner decision 2026-10-19). It is applied
 * after the trial row, like `MUSIC24_TRIAL_OUTREACH`, so the trial row keeps the shape that the
 * native `PlanTableParityTest` tests read.
 */
export const MUSIC24_TRIAL_REELS_PLAN_MAX_POSTS_PER_DAY = 1;

export const music24PlanCapabilities: Record<Music24PlanTier, Music24Capabilities> = {
  ...music24PlanCapabilitiesBeforeOutreach,
  trial: {
    ...music24PlanCapabilitiesBeforeOutreach.trial,
    ...MUSIC24_TRIAL_OUTREACH,
    ...MUSIC24_TRIAL_PRO_EXCEPTIONS,
    reelsPlanMaxPostsPerDay: MUSIC24_TRIAL_REELS_PLAN_MAX_POSTS_PER_DAY,
  },
};

/** The tiers that a person pays for. `plug` is a solo tier and sells on the web only. */
export type Music24PaidPlanTier = Exclude<Music24PlanTier, 'free' | 'trial'>;

/**
 * The team tiers (Max and Ultra). Every paid plan but Plug sells extra artists since 2026-10-23
 * (`planHasExtraArtistAddOn`); this type stays for the Outreach mailbox pool and the team copy.
 */
export type Music24TeamPlanTier = 'manager' | 'label';

/**
 * The monthly price of each paid plan in whole US dollars. EUR is the same number, and the App
 * Store and Google Play price of Artist and Pro is the same number too. Stripe prices
 * (music24-api `music24Prices.ts`) and the apps read it from here.
 */
export const MUSIC24_MONTHLY_PRICE_USD: Readonly<Record<Music24PaidPlanTier, number>> = {
  artist: 19,
  pro: 39,
  plug: 299,
  manager: 69,
  label: 249,
};

/** An extra artist costs the plan price minus this share (owner decision 2026-10-23, D1). */
export const MUSIC24_EXTRA_ARTIST_DISCOUNT = 0.2;

/**
 * The monthly price of one extra artist on `tier`, in US cents (EUR the same number): the plan
 * price minus `MUSIC24_EXTRA_ARTIST_DISCOUNT`. Plus 1520, Pro 3120, Max 5520, Ultra 19920. A year
 * costs `MUSIC24_MONTHS_PER_YEARLY_PRICE` months. Pure.
 */
export function music24ExtraArtistMonthlyCents(tier: Music24PaidPlanTier): number {
  return Math.round(MUSIC24_MONTHLY_PRICE_USD[tier] * 100 * (1 - MUSIC24_EXTRA_ARTIST_DISCOUNT));
}

/**
 * The limits that each artist of a workspace gets in full (owner decision 2026-10-23, D2): with N
 * artists (1 included plus the extra artists) the workspace limit is the plan value times N. The
 * other limits are for the workspace: the seats are by tier (D4), the social accounts and the X
 * posts are already per account, and the analytics browse limits are per person. The Outreach
 * numbers are not here: the Outreach lane owns them (`music24OutreachPoolLimit`).
 */
export const MUSIC24_PER_ARTIST_LIMITS = [
  'scheduledPostsPerMonth',
  'videoCreditsPerMonth',
  'aiInsightsPerMonth',
  'activeReleasesLimit',
] as const satisfies readonly (keyof Music24Capabilities)[];

/** A year costs this many months, for the plan and for the extra artists. */
export const MUSIC24_MONTHS_PER_YEARLY_PRICE = 10;

export function isMusic24TeamPlanTier(tier: Music24PlanTier): tier is Music24TeamPlanTier {
  return tier === 'manager' || tier === 'label';
}

/**
 * The plans with the ProRes download of the motion artwork (owner decision 2026-10-08: "Pro or
 * higher"): Pro, Plug, Manager and Label. Not Free, not Artist, not the trial (the files are 1 to 4 GB).
 */
export const PRORES_DOWNLOAD_PLANS: readonly Music24PlanTier[] = ['pro', 'plug', 'manager', 'label'];

/** True when the plan may make the ProRes MOV of a motion artwork file ("Download ProRes for distributor"). */
export function canDownloadProRes(plan: Music24PlanTier): boolean {
  return PRORES_DOWNLOAD_PLANS.includes(plan);
}

export type Music24QuotaKind =
  | 'scheduledPost'
  | 'videoCredit'
  | 'aiInsight'
  | 'coverAiPicture'
  | 'outreachReveal'
  | 'outreachSend';

/**
 * Credits one video takes from the shared pool. `aiClip`: one AI clip of the music video editor and
 * one shot of a directed video (8 s, 1080p). The rule of spec 2026-10-09 D9: the credits cover the
 * model price at the cheapest credit (Label, about $0.28). Wan 3.0 (owner decision 2026-10-22):
 * $1.60 a clip, so 6 credits ($1.67); it was 4 for Veo ($0.96).
 */
export const VIDEO_CREDIT_COST = { short: 1, fullSong: 3, aiClip: 6 } as const;

/** The clip length that `VIDEO_CREDIT_COST.aiClip` pays for: 6 credits for 8 s, so 0.75 credits a second. */
export const AI_CLIP_CREDIT_SECONDS = 8;

/**
 * The credits of one AI clip of `seconds` (owner decision 2026-10-23: a price per second, rounded up
 * for each clip). 3 s → 3, 4 s → 3, 8 s → 6, 12 s → 9, 15 s → 12. Pure.
 */
export function aiClipCreditsFor(seconds: number): number {
  const length = Number.isFinite(seconds) && seconds > 0 ? seconds : AI_CLIP_CREDIT_SECONDS;
  return Math.ceil((length * VIDEO_CREDIT_COST.aiClip) / AI_CLIP_CREDIT_SECONDS - 1e-9);
}

/**
 * The video credits of one AI reel: one short video (owner decision 2026-10-08: one quality, no 2x
 * credits). The server charges with it and the Studio shows it. Pure.
 */
export function reelCredits(): number {
  return VIDEO_CREDIT_COST.short;
}

export function music24QuotaLimit(capabilities: Music24Capabilities, kind: Music24QuotaKind): number | null {
  switch (kind) {
    case 'scheduledPost':
      return capabilities.scheduledPostsPerMonth;
    case 'videoCredit':
      return capabilities.videoCreditsPerMonth;
    case 'aiInsight':
      return capabilities.aiInsightsPerMonth;
    case 'coverAiPicture':
      // Not a field of the capabilities (the native plan parity): the table of the cover editor.
      return coverAiLimit(capabilities.plan);
    case 'outreachReveal':
      return capabilities.outreachRevealsPerMonth;
    case 'outreachSend':
      return capabilities.outreachSendsPerMonth;
  }
}

/**
 * The workspace limit of the Outreach mailboxes. A team plan (Manager, Label) gives the number for
 * each artist, and the workspace pools it. A solo plan gives the number once.
 *
 * Use it for the own mailboxes (`outreachMailboxes`) and the pitches a day
 * (`outreachPitchesPerDay`). The pitch credits and the sends of a month are for the whole
 * workspace on every plan (spec 2026-10-13): read `outreachRevealsPerMonth` and
 * `outreachSendsPerMonth` as they are.
 */
export function music24OutreachPoolLimit(
  capabilities: Music24Capabilities,
  perArtist: number,
  artistCount: number
): number {
  return isMusic24TeamPlanTier(capabilities.plan) ? perArtist * Math.max(1, artistCount) : perArtist;
}

/** Quotas reset on the 1st of the calendar month, UTC. */
export function music24QuotaPeriodStart(now: Date): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
}

export function music24QuotaResetsAt(now: Date): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
}
